import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import {
  carrierAndServiceSchema,
  DynamicRateSelectionPreset,
} from "../integration/redo-in-shipping/shipping-rate-table";
import { InsertionPosition } from "../widget";
import {
  deliveryDateRangeSchema,
  type DeliveryDateRange,
} from "./delivery-date/format-delivery-date";

export enum DateOrigin {
  CARRIER_RATE = "CARRIER_RATE",
  BUSINESS_DAYS_IN_FUTURE = "BUSINESS_DAYS_IN_FUTURE",
}

export const DEFAULT_BUSINESS_DAYS_IN_FUTURE = 5;
export type StorefrontDeliveryEstimateDateRange = DeliveryDateRange;

export enum SelectorType {
  PRODUCT_PAGE = "PRODUCT_PAGE",
  CART = "CART",
}

/**
 * Schema for carrier rate configuration in storefront delivery estimate
 */
const storefrontDeliveryEstimateCarrierRateSchema_ = z.object({
  rates: z.array(carrierAndServiceSchema).optional(),
  multipleDynamicRateSelection: createEnum(
    [DynamicRateSelectionPreset.CHEAPEST, DynamicRateSelectionPreset.FASTEST],
    {
      enumName: "DynamicRateSelectionPreset",
      isPartial: true,
      originalEnum: DynamicRateSelectionPreset,
    },
  ).optional(),
});
export const storefrontDeliveryEstimateCarrierRateSchema: z.ZodType<StorefrontDeliveryEstimateCarrierRate> =
  storefrontDeliveryEstimateCarrierRateSchema_;
export type StorefrontDeliveryEstimateCarrierRate = z.infer<
  typeof storefrontDeliveryEstimateCarrierRateSchema_
>;

/**
 * Schema for date origin configuration in storefront delivery estimate
 */
const dateOriginSchema_ = z.union([
  z.object({
    origin: z.literal(DateOrigin.CARRIER_RATE),
    carrierRate: storefrontDeliveryEstimateCarrierRateSchema,
  }),
  z.object({
    origin: z.literal(DateOrigin.BUSINESS_DAYS_IN_FUTURE),
    businessDaysInFuture: z
      .number()
      .optional()
      .default(DEFAULT_BUSINESS_DAYS_IN_FUTURE),
    range: deliveryDateRangeSchema.optional(),
  }),
]);
export const dateOriginSchema: z.ZodType<DateOriginSettings> =
  dateOriginSchema_;
export type DateOriginSettings = z.infer<typeof dateOriginSchema_>;

/**
 * Schema for delivery estimate selector configuration
 */
export const deliveryEstimateSelectorSchema = z.object({
  _id: zExt.objectId(),
  selector: z.string(),
  insertPosition: createEnum(InsertionPosition, {
    enumName: "InsertionPosition",
  }),
});
export type DeliveryEstimateSelector = z.infer<
  typeof deliveryEstimateSelectorSchema
>;

/**
 * Main schema for storefront delivery estimate settings
 */
const storefrontDeliveryEstimateSchema_ = z.object({
  enabled: z.boolean(),
  selectors: z.array(deliveryEstimateSelectorSchema).optional(),
  cartSelectors: z.array(deliveryEstimateSelectorSchema).optional(),
  dateOrigin: dateOriginSchema,
});
export const storefrontDeliveryEstimateSchema: z.ZodType<StorefrontDeliveryEstimate> =
  storefrontDeliveryEstimateSchema_;
export type StorefrontDeliveryEstimate = z.infer<
  typeof storefrontDeliveryEstimateSchema_
>;
